import React, { useState, useEffect, useRef } from "react";
import * as d3 from "d3";

/* styles */
import "/styles/modern-normalize.css";
import "/styles/global.css";
import "/styles/components/foodimpactdiagram.css";
import "/styles/utility.css";

const columnMap = {
  water: "Water withdrawals per kilogram",
  land: "Land use per kilogram",
  emissions: "Emissions per kilogram",
};

const FoodImpactDiagram = () => {
  const [measure, setMeasure] = useState("water");
  const [nodes, setNodes] = useState([]);
  const [columnData, setColumnData] = useState([]);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 300, height: 300 });
  const [categoryFilter, setCategoryFilter] = useState("all");

  const colorScale = d3
    .scaleOrdinal()
    .domain([
      "Baked Goods",
      "Fruits",
      "Dairy & Alternatives",
      "Beverages",
      "Protein",
    ])
    .range([
      "hsl(24, 7%, 65%)",
      "hsl(24, 7%, 55%)",
      "hsl(24, 7%, 45%)",
      "hsl(24, 7%, 35%)",
      "hsl(24, 7%, 25%)",
    ]);

  // Load CSV data on mount
  useEffect(() => {
    d3.csv("/data/Joined_Food_Impact_Table.csv", d3.autoType).then(
      (loadedData) => {
        setColumnData(loadedData);
      }
    );
  }, []);

  // Responsive container
  useEffect(() => {
    const ro = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect;
        setDimensions({ width: height * 1.3, height });
      }
    });
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Build treemap layout
  useEffect(() => {
    const { width, height } = dimensions;
    if (!width || !height || columnData.length === 0) return;

    const filteredData =
      categoryFilter === "all"
        ? columnData
        : columnData.filter((d) => d["Food Category"] === categoryFilter);

    const grouped = d3
      .groups(filteredData, (d) => d["Food Category"])
      .map(([category, items]) => ({ name: category, children: items }));

    const root = d3
      .hierarchy({ children: grouped })
      .sum((d) =>
        d[columnMap[measure]] !== undefined ? d[columnMap[measure]] : 0
      );

    d3.treemap().size([width, height]).padding(3)(root);

    const targetNodes = root.leaves().map((d) => ({
      id: `${d.parent.data.name}-${d.data.name}`,
      x0: d.x0,
      y0: d.y0,
      x1: d.x1,
      y1: d.y1,
      category: d.parent.data.name,
      name: d.data["Entity"],
      value: d.value,
    }));

    if (nodes.length === 0) {
      setNodes(targetNodes);
      return;
    }

    const interpolators = targetNodes.map((target) => {
      const current = nodes.find((n) => n.id === target.id) || target;
      return {
        id: target.id,
        category: target.category,
        name: target.name,
        value: target.value,
        interpX0: d3.interpolate(current.x0, target.x0),
        interpY0: d3.interpolate(current.y0, target.y0),
        interpX1: d3.interpolate(current.x1, target.x1),
        interpY1: d3.interpolate(current.y1, target.y1),
      };
    });

    let start = null;
    function animateFrame(timestamp) {
      if (!start) start = timestamp;
      const elapsed = timestamp - start;
      const t = Math.min(elapsed / 300, 1);

      const newNodes = interpolators.map((interp) => ({
        id: interp.id,
        category: interp.category,
        name: interp.name,
        value: interp.value,
        x0: interp.interpX0(t),
        y0: interp.interpY0(t),
        x1: interp.interpX1(t),
        y1: interp.interpY1(t),
      }));

      setNodes(newNodes);
      if (t < 1) requestAnimationFrame(animateFrame);
    }

    requestAnimationFrame(animateFrame);
  }, [measure, dimensions, categoryFilter, columnData]);

  return (
    <div className="treemap__container" ref={containerRef}>
      <h2 className="diagram__header">Food Impact Diagram</h2>
      <h2 className="diagram__subheader">
        {categoryFilter}, {measure}
      </h2>
      <p className="diagram__text">
        This treemap shows the environmental impact of ingredients. Each
        rectangle represents a food, sized by impact per kilogram of ingredient.
        Use the buttons below to select different measures (water use, land use,
        emissions produced) or filter by categories.
      </p>

      <svg viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}>
        {nodes.map((node, i) => (
          <g key={i} transform={`translate(${node.x0},${node.y0})`}>
            <rect
              className="treemap-rect"
              style={{ fill: colorScale(node.category) }}
              width={node.x1 - node.x0}
              height={node.y1 - node.y0}
              rx={5}
              ry={5}
            />
            <text x={4} y={14} className="treemap-rect__text">
              <tspan x={4} dy="0.5em">
                {node.name}
              </tspan>
              <tspan x={4} dy="1.2em">
                {Number(node.value).toFixed(2)}
                {measure === "water" && " L"}
                {measure === "land" && " m²"}
                {measure === "emissions" && " kgCO₂"}
              </tspan>
            </text>
          </g>
        ))}
      </svg>

      <div className="treemap__button-container">
        <h4 className="diagram__text-bold">Select a Measure</h4>
        <div className="treemap__buttons">
          <button onClick={() => setMeasure("water")}>Water</button>
          <button onClick={() => setMeasure("land")}>Land</button>
          <button onClick={() => setMeasure("emissions")}>Emissions</button>
        </div>

        <h4 className="diagram__text-bold">Filter by Category</h4>
        <div className="treemap__buttons">
          <button onClick={() => setCategoryFilter("all")}>All</button>
          <button onClick={() => setCategoryFilter("Fruits")}>Fruits</button>

          <button onClick={() => setCategoryFilter("Baked Goods")}>
            Baked Goods
          </button>
          <button onClick={() => setCategoryFilter("Protein")}>Protein</button>

          <button onClick={() => setCategoryFilter("Dairy & Alternatives")}>
            Dairy & Alternatives
          </button>
          <button onClick={() => setCategoryFilter("Beverages")}>
            Beverages
          </button>
        </div>
      </div>
    </div>
  );
};

export default FoodImpactDiagram;
